/**
 * FamilyMemberPhotoEditor Component
 *
 * Allows uploading, changing, or removing profile photos for family members.
 * Uses expo-image-picker for image selection.
 */

import { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { Spacing, BorderRadius } from '@/constants/spacing';

type FamilyMemberPhotoEditorProps = {
  imageUri: string | null;
  name: string;
  color: string;
  accentColor: string;
  textColor: string;
  backgroundColor: string;
  onPhotoChanged: (uri: string | null) => void;
};

export function FamilyMemberPhotoEditor({
  imageUri,
  name,
  color,
  accentColor,
  textColor,
  backgroundColor,
  onPhotoChanged,
}: FamilyMemberPhotoEditorProps) {
  const [isUploading, setIsUploading] = useState(false);
  const initial = name.charAt(0).toUpperCase();

  const requestPermissions = async (): Promise<boolean> => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    return status === 'granted';
  };

  const handleUploadPhoto = async () => {
    try {
      setIsUploading(true);

      const hasPermission = await requestPermissions();
      if (!hasPermission) {
        Alert.alert('Permission Required', 'Please grant access to your photo library.');
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]?.uri) {
        onPhotoChanged(result.assets[0].uri);
      }
    } catch (error) {
      console.error('Failed to upload photo:', error);
      Alert.alert('Error', 'Failed to upload photo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    Alert.alert(
      'Remove Photo',
      'Are you sure you want to remove this profile photo?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => onPhotoChanged(null),
        },
      ]
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.avatarContainer}>
        {imageUri ? (
          <Image source={{ uri: imageUri }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatarPlaceholder, { backgroundColor: color }]}>
            <Text style={styles.avatarInitial}>{initial}</Text>
          </View>
        )}
      </View>

      <View style={styles.buttonsContainer}>
        <TouchableOpacity
          style={[styles.button, { backgroundColor }]}
          onPress={handleUploadPhoto}
          disabled={isUploading}
          activeOpacity={0.7}
        >
          <Ionicons
            name={imageUri ? 'camera-outline' : 'cloud-upload-outline'}
            size={20}
            color={accentColor}
            style={styles.buttonIcon}
          />
          <Text style={[styles.buttonText, { color: textColor }]}>
            {isUploading
              ? 'Uploading...'
              : imageUri
              ? 'Change Photo'
              : 'Upload Photo'}
          </Text>
        </TouchableOpacity>

        {imageUri && (
          <TouchableOpacity
            style={[styles.button, styles.removeButton, { backgroundColor }]}
            onPress={handleRemovePhoto}
            activeOpacity={0.7}
          >
            <Ionicons
              name="trash-outline"
              size={20}
              color="#ff3b30"
              style={styles.buttonIcon}
            />
            <Text style={[styles.buttonText, styles.removeButtonText]}>Remove</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingVertical: Spacing.lg,
  },
  avatarContainer: {
    marginBottom: Spacing.lg,
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    resizeMode: 'cover',
  },
  avatarPlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitial: {
    fontSize: 40,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: Spacing.sm,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    borderRadius: BorderRadius.md,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  buttonIcon: {
    marginRight: Spacing.xs,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  removeButton: {
    borderColor: '#ff3b3033',
  },
  removeButtonText: {
    color: '#ff3b30',
  },
});
